import {
  createCodeableConcept,
  createFhirDate,
  createFhirDateTime,
  createReferenceFromResource,
  type DeviceCode,
  DeviceLifecycleMap,
  DeviceUseStatementCode,
  getDeviceFriendlyName,
  getDeviceUseStatementOverlapPeriods,
  getFirstCodeFromCodableConcepts,
  getPatientNameDisplay,
  isDeviceUseStatementActive,
  isDeviceUseStatementOfType,
  mapFhirToModelPeriod,
  type WithId,
} from '@quromedical/fhir-common';
import {
  compact,
  concat,
  type Fn,
  type Interval,
  IntervalSchema,
} from '@quromedical/utils';
import type { DeviceUseStatement, Period } from 'fhir/r4';
import { z } from 'zod';
import { getValidReference } from '../mappings/reference';
import { type MeasurementName, MeasurementNameSchema } from '../vitals';

import type { DeviceAssociation, DeviceAssociationWithId } from './schema';

type DeviceUseStatementStatus = DeviceUseStatement['status'];

export const deviceCodeToDeviceUseStatementCode: Record<
  DeviceCode,
  DeviceUseStatementCode
> = {
  'device.patch': DeviceUseStatementCode.patch,
  'device.band': DeviceUseStatementCode.band,
  'device.bloodpressure': DeviceUseStatementCode.bloodPressure,
  'device.glucose': DeviceUseStatementCode.glucose,
  'device.relay': DeviceUseStatementCode.relay,
};

export const TimeSeriesPatientQuerySchema = z.object({
  patientId: z.string().uuid(),
  interval: IntervalSchema,
  measurementNames: MeasurementNameSchema.array().readonly(),
});

export type TimeSeriesPatientQuery = z.infer<
  typeof TimeSeriesPatientQuerySchema
>;

export interface TimeSeriesQuery {
  sensorId: string;
  interval: Interval;
  measurementNames: readonly MeasurementName[];
}

const endDeviceUseStatement = (
  deviceUseStatement: DeviceUseStatement,
  status: DeviceUseStatementStatus,
): DeviceUseStatement => {
  const { timingPeriod, timingDateTime } = deviceUseStatement;

  const end = createFhirDateTime(new Date());

  // default the timingPeriod is undefined for patches that were not created with a timing period.
  // we need to default the start time to the timingDateTime.
  const start = timingPeriod?.start || timingDateTime;

  const newPeriod: Period = {
    start,
    end,
  };

  const result: DeviceUseStatement = {
    ...deviceUseStatement,
    status,
    timingPeriod: newPeriod,
    timingDateTime: undefined,
  };

  return result;
};

export const completeDisposableDeviceUseStatements = (
  deviceUseStatements: DeviceUseStatement[],
): DeviceUseStatement[] => {
  const disposableDeviceUseStatements = deviceUseStatements.filter(
    (deviceUseStatement) => {
      if (deviceUseStatement.status !== 'active') {
        return false;
      }
      const deviceUseStatementCode = getFirstCodeFromCodableConcepts(
        deviceUseStatement.reasonCode,
      );

      if (!deviceUseStatementCode) {
        return false;
      }

      const recyclable =
        DeviceLifecycleMap[deviceUseStatementCode as DeviceUseStatementCode];

      return recyclable.lifecycle === 'disposable';
    },
  );

  const updatedDeviceUseStatements = disposableDeviceUseStatements.map(
    (deviceUseStatement) =>
      endDeviceUseStatement(deviceUseStatement, 'stopped'),
  );

  return updatedDeviceUseStatements;
};

export const completeReusableDeviceUseStatements = (
  deviceUseStatements: DeviceUseStatement[],
): DeviceUseStatement[] => {
  const reusableDeviceUseStatements = deviceUseStatements.filter(
    (deviceUseStatement) => {
      if (deviceUseStatement.status !== 'active') {
        return false;
      }

      const deviceUseStatementCode = getFirstCodeFromCodableConcepts(
        deviceUseStatement.reasonCode,
      );

      if (!deviceUseStatementCode) {
        return false;
      }

      const recyclable =
        DeviceLifecycleMap[deviceUseStatementCode as DeviceUseStatementCode];

      return recyclable.lifecycle === 'reusable';
    },
  );

  const updatedDeviceUseStatements = reusableDeviceUseStatements.map(
    (deviceUseStatement) =>
      endDeviceUseStatement(deviceUseStatement, 'completed'),
  );

  return updatedDeviceUseStatements;
};

export const endDeviceUseStatements = (
  deviceUseStatements: DeviceUseStatement[],
): DeviceUseStatement[] => {
  const deviceUseStatementsResult = concat(
    completeReusableDeviceUseStatements(deviceUseStatements),
    completeDisposableDeviceUseStatements(deviceUseStatements),
  );

  return deviceUseStatementsResult;
};

/**
 * Determines if there are relay devices in a list of device use statements
 * @param deviceUseStatement a device use statement list
 * @returns true if there is a relay device in the list otherwise false.
 */
export const hasAnyActiveDeviceOfType = (
  deviceUseStatements: DeviceUseStatement[] = [],
  deviceUseStatementType: DeviceUseStatementCode,
): boolean => {
  const filteredDeviceUseStatements = deviceUseStatements.filter((d) =>
    isDeviceUseStatementOfType(d, deviceUseStatementType),
  );

  return filteredDeviceUseStatements.some(isDeviceUseStatementActive);
};

export const mapCreateModelToDeviceUseStatement = (
  patient: WithId<fhir4.Patient>,
  device: WithId<fhir4.Device>,
  code: DeviceUseStatementCode,
): fhir4.DeviceUseStatement => ({
  resourceType: 'DeviceUseStatement',
  status: 'active',
  device: createReferenceFromResource(device, getDeviceFriendlyName(device)),
  subject: createReferenceFromResource(patient, getPatientNameDisplay(patient)),
  reasonCode: [createCodeableConcept(code)],
  timingPeriod: {
    start: createFhirDate(new Date()),
  },
});

export const mapModelToDeviceUseStatement = (
  model: DeviceAssociation,
): fhir4.DeviceUseStatement => ({
  resourceType: 'DeviceUseStatement',
  status: model.status,
  device: model.device,
  subject: model.patient,
  reasonCode: [createCodeableConcept(model.code)],
  timingPeriod: model.period,
});

export const mapDeviceUseStatementToModel = (
  fhir: WithId<fhir4.DeviceUseStatement>,
): DeviceAssociationWithId => ({
  id: fhir.id,
  status: fhir.status,
  device: getValidReference(fhir.device),
  patient: getValidReference(fhir.subject),
  code: getFirstCodeFromCodableConcepts(fhir.reasonCode),
  period: mapFhirToModelPeriod(fhir.timingPeriod),
});

interface MapDeviceUseStatementsToTimeSeriesQueriesParams {
  deviceUseStatements: fhir4.DeviceUseStatement[];
  start: Date;
  end: Date;
  measurementNames: readonly MeasurementName[];
}

export const mapDeviceUseStatementsToTimeSeriesQueries: Fn<
  MapDeviceUseStatementsToTimeSeriesQueriesParams,
  TimeSeriesQuery[]
> = ({
  deviceUseStatements,
  measurementNames,
  start,
  end,
}): TimeSeriesQuery[] => {
  const associations = getDeviceUseStatementOverlapPeriods({
    deviceUseStatements,
    from: start.getTime(),
    to: end.getTime(),
  });

  const queries = compact(associations).map<TimeSeriesQuery>(
    ({ start, end, deviceName }) => ({
      interval: {
        start,
        end,
      },
      sensorId: deviceName,
      measurementNames,
    }),
  );

  return queries;
};
